import { type EffectCallback, useEffect, useRef } from 'react';

export type OnValueInit<T> = (value: T) => void;

export type UseInitArgs<T> = {
  onInit?: EffectCallback;
  value?: T;
  onValueInit?: OnValueInit<T>;
  onCleanup?: () => void;
};

/**
 * Invokes the `onInit` callback once, as a shorthand for useEffect with no dependencies.
 * Invokes the `onValueInit` callback when `value` gets a value for the first time.
 */
export default function useInit<T>(args: UseInitArgs<T>) {
  const { value, onInit, onValueInit, onCleanup } = args;
  const initializedRef = useRef(false);
  const onValueInitRef = useRef(onValueInit);
  onValueInitRef.current = onValueInit;

  // biome-ignore lint/correctness/useExhaustiveDependencies: Run once
  useEffect(() => {
    if (!initializedRef.current && value !== undefined) {
      initializedRef.current = true;
      if (onValueInitRef.current) {
        onValueInitRef.current(value);
      }
    }

    return onCleanup;
  }, [value]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Run once
  useEffect(() => {
    if (onInit) {
      return onInit();
    }
  }, []);
}
